import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import { GUIDE_ICON } from '@/components/GuideIcons';
import { PRODUCT_DETAILS, PRODUCT_PAGE } from '@/data/mockData';
// [2026-10-23 사용자] 제품 서브 화이트 전환(격리) — 화이트 전용 모듈. 다크 공유 모듈 보존(홈·InstallPages 안전).
import s from './ProductWhite.module.scss';   // 공통 제품상세 스타일(화이트)
import t from './ProductInstrumentsWhite.module.scss'; // 탭·구매·링크카드(화이트)

// [2026-10-08 사용자] 다크 카드 섹션의 절제된 라인 아이콘(GUIDE_ICON + t.cardIcon 밝은 스트로크).
const Ic = ({ n }: { n: string }) => <div className={t.cardIcon}>{GUIDE_ICON[n]}</div>;

// ============================================================================
//  제품 상세 — 환경 계측기 4종  (/products/instruments)
//  [2026-10-08] 기획서 v7 2-3. 7개 콘텐츠 영역 순서 + 계측기 선택 '탭'.
//    실데이터: ①선택 ②적용시설 ③제품정보·사양 ⑥게이트웨이 연동  (PRODUCT_DETAILS i1~i4)
//    준비중(회사 자료 대기): ④설치방법(배선도·설치사진) ⑤점검·교정(상세 주기·소모품)
//  공통 스타일은 ProductEvsG200.module.scss 재사용 → 제품 상세 '공통 규격' 축적.
// ============================================================================

const HERO_IMG = '/images/products/products-hero.webp'; // [2026-10-11] 제품 메뉴 히어로 이미지 통일
// [2026-10-11 사용자] 서브 슬로건 풍성화(문서 대표제목 2-3 핵심 유지+확장). 히어로 중앙 충분히 차게.
const SLOGAN = '전류·차압·온도·pH 등 배출·방지시설의 가동상태를 정밀 측정하고, EVS-G200 게이트웨이와 연동해 시설이 제대로 작동하는지 실시간으로 확인하는 환경 계측기입니다.';

const FEAT_ICON: Record<string, React.ReactNode> = {
  current: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  pressure: <><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /><path d="M12 12l3.5-2.2" /><path d="M12 8v1" /></>,
  temp: <path d="M10 13V5a2 2 0 1 1 4 0v8a4 4 0 1 1-4 0z" />,
  ph: <><path d="M9 3h6M10 3v5l-5.2 9.2A2 2 0 0 0 6.6 20h10.8a2 2 0 0 0 1.8-2.8L14 8V3" /><path d="M7.5 14h9" /></>,
  comm: <><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8 15.5a6 6 0 0 1 8 0" /><path d="M12 18.5h.01" /></>,
  gauge: <><path d="M20.5 12a8.5 8.5 0 1 0-17 0" /><path d="M12 12l4-2.5" /></>,
  check: <><path d="M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>,
};

// [2026-10-23 사용자] ③ 제품정보·사양을 evs-g200 상세사양과 동일 스타일(전폭 웜 밴드 + 제원 리스트)로.
//   사양 키→심플 라인 아이콘 매핑(미매칭=solution 폴백). 계측기별 키 편차 대비 폭넓게.
const SPEC_ICON: Record<string, string> = {
  '측정 대상': 'gauge', '측정 항목': 'gauge', '측정 범위': 'gauge', '측정': 'gauge',
  '센서 방식': 'solution', '센서 타입': 'solution', '센서': 'solution', '방식': 'solution',
  '출력': 'signal', '출력 신호': 'signal', '신호': 'signal', '통신': 'signal',
  '게이트웨이 연동': 'gateway', '연동': 'gateway', '게이트웨이': 'gateway',
  '전원': 'energy', '정확도': 'certified', '인증': 'certified', '응답속도': 'cycle', '재질': 'solution',
};

// 계측기 4종 — id=PRODUCT_DETAILS 키(i1~i4). 적용시설·연동은 검증 사실(blurb/specs) 기반.
const INST = [
  { id: 'i1', label: '전류계', en: 'Current Meter', image: '/images/products/product-1-f.jpg',
    facility: '송풍·배출설비 (방지시설 가동)', why: '방지시설이 실제 가동 중인지 송풍·배출 전류로 판정합니다.', connect: 'DC 4~20mA' },
  { id: 'i2', label: '차압계', en: 'Differential Pressure', image: '/images/products/product-2-f.jpg',
    facility: '집진·여과 방지시설', why: '필터 전후단 압력차(Pa)로 막힘·이상을 조기에 파악합니다.', connect: 'RS-485' },
  { id: 'i3', label: '온도계', en: 'Temperature', image: '/images/products/product-3-f.jpg',
    facility: '배출가스·설비', why: '유입구 등 핵심 지점의 공정 온도를 실시간 감시합니다.', connect: 'DC 4~20mA (온도센서 1CH)' },
  { id: 'i4', label: 'pH계', en: 'pH Meter', image: '/images/products/product-4-f.jpg',
    facility: '흡수·세정 방지시설', why: '수소이온농도(pH)·ORP로 처리 효율과 약품 투입 상태를 감시합니다.', connect: 'RS-485' },
];

const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

export default function ProductInstruments() {
  const [idx, setIdx] = useState(0);
  const cur = INST[idx];
  const detail = PRODUCT_DETAILS[cur.id];
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  const gw = PRODUCT_PAGE.gateway;

  return (
    <main className={s.page}>
      <PageLoader />

      {/* 히어로 */}
      <header className={s.hero}>
        <div className={s.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '제품', '환경 계측기']} />
        <div className={s.heroInner}>
          <p className={s.heroEyebrow}>INSTRUMENTS</p>
          <h1 className={s.heroTitle}>환경 계측기 4종</h1>
          <p className={s.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 계측기 선택 — 탭 + 선택 제품 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>SELECT</p>
          <h2 className={s.h2c}>계측기를 선택하세요</h2>
          <div className={t.tabs} role="tablist">
            {INST.map((it, i) => (
              <button key={it.id} role="tab" aria-selected={i === idx}
                className={`${t.tab} ${i === idx ? t.on : ''}`} onClick={() => setIdx(i)}>
                {it.label}
              </button>
            ))}
          </div>
          <div className={s.first}>
            <div className={s.firstMedia}><img src={cur.image} alt={cur.label} /></div>
            <div className={s.firstBody}>
              <span className={s.badge}>{cur.en}</span>
              <h2 className={s.h2}>{cur.label}</h2>
              <p className={s.lead}>{detail?.blurb}</p>
              <div className={s.chips}><span className={s.chip}><b>적용</b>{cur.facility}</span><span className={s.chip}><b>연동</b>{cur.connect}</span></div>
              <div className={s.ctaRow}>
                <Button variant="pill" onClick={() => nav('/support/inquiry?product=' + encodeURIComponent('환경 계측기') + '&from=/products/instruments')}>제품 문의</Button>
                <a className={s.ctaGhost} onClick={() => nav('/support/consult')}>구성 견적 상담</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ② 적용시설 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>APPLICATION</p>
          <h2 className={s.h2c}>어떤 시설에, 왜 필요한가</h2>
          <p className={s.sub}><b style={{ color: '#1b2430' }}>{cur.label}</b> — {cur.why} 적용 시설: {cur.facility}.</p>
          <a className={t.linkInline} onClick={() => nav('/guide')}>배출·방지시설 안내 보기 →</a>
        </Reveal>
      </section>

      {/* ③ 제품정보·사양 — [2026-10-23 사용자] ★제품 스펙 공통 스타일: evs-g200과 동일 전폭 웜 밴드 + 제원 리스트 */}
      <section className={`${s.section} ${s.specBand}`}>
        <Reveal>
          <p className={s.eyebrow}>SPECIFICATIONS</p>
          <h2 className={s.h2c}>{cur.label} 제품정보·사양</h2>
          <div className={s.specList}>
            {detail?.specs?.map((sp, i) => (
              <div key={i} className={s.specRow}>
                <div className={s.specIco}>{GUIDE_ICON[SPEC_ICON[sp.k] || 'solution']}</div>
                <span className={s.specKey}>{sp.k}</span>
                <span className={s.specVal}>{sp.v}</span>
              </div>
            ))}
          </div>
          <ul className={s.featList}>
            {detail?.features?.map((f, i) => (
              <li key={i} className={s.feat}>
                <span className={s.featIco} aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    {FEAT_ICON[f.icon] || FEAT_ICON.check}
                  </svg>
                </span>
                <span className={s.featTx}><strong>{f.title}</strong><em>{f.desc}</em></span>
              </li>
            ))}
          </ul>
          <p className={s.note}>※ 측정범위·정확도 등 상세 전기사양은 제조사 데이터시트 확보 후 보강합니다.</p>
        </Reveal>
      </section>

      {/* ④ 설치방법 — 준비중 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>INSTALLATION</p>
          <h2 className={s.h2c}>설치 방법</h2>
          <p className={s.sub}>센서·배관·보호관·전극 설치 예시와 배선도를 이 영역에 게시합니다.</p>
          <div className={s.soon} role="img" aria-label="설치 배선도 준비중">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            <span>설치사진·배선도 · 준비중</span>
          </div>
        </Reveal>
      </section>

      {/* ⑤ 점검·교정 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>MAINTENANCE</p>
          <h2 className={s.h2c}>점검 · 교정</h2>
          <p className={s.sub}>정기 점검(통신·배선·오염 상태), 센서 교정, 소모품 교체 기준을 안내합니다. 계측기별 상세 교정·교체 주기는 자료실의 사양서·설명서에서 확인하실 수 있습니다.</p>
          <div className={s.soon} role="img" aria-label="교정 주기 준비중">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            <span>교정·소모품 교체주기 표 · 준비중</span>
          </div>
          <a className={t.linkInline} onClick={() => nav('/support/faq')}>FAQ · 자료실 보기 →</a>
        </Reveal>
      </section>

      {/* ⑥ 게이트웨이 연동 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>GATEWAY</p>
          <h2 className={s.h2c}>EVS-G200 게이트웨이와 연동</h2>
          <p className={s.sub}>전류·온도계는 DC 4~20mA, 차압·pH계는 RS-485로 게이트웨이(EVS-G200)와 연동되어, 측정값이 수집·표시·저장되고 환경부 GreenLink로 전송됩니다.</p>
          <a className={t.linkCard} onClick={() => nav('/products/evs-g200')}>
            <img src={gw.image} alt="EVS-G200" />
            <div><strong>IoT Gateway {gw.name}</strong><em>계측기 데이터를 수집·전송하는 IoT 게이트웨이</em></div>
            <span>→</span>
          </a>
        </Reveal>
      </section>

      {/* ⑦ 구매방식 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>PURCHASE</p>
          <h2 className={s.h2c}>구매 방식</h2>
          <div className={t.buyGrid}>
            <div className={t.buyCard}><Ic n="gauge" /><h3>단품 구매</h3><p>계측기 단품으로 구매하실 수 있습니다. 모델·수량·연동 환경을 알려주시면 견적을 안내합니다.</p></div>
            <div className={t.buyCard}><Ic n="building" /><h3>설치 포함 (현장실사형)</h3><p>게이트웨이 구성·설치·연동까지 일괄 수행합니다. 현장실사 후 최적 구성과 견적을 제안합니다.</p></div>
          </div>
          <div className={s.ctaRow} style={{ marginTop: 28 }}>
            <Button variant="pill" onClick={() => nav('/support/inquiry?product=' + encodeURIComponent('환경 계측기') + '&from=/products/instruments')}>구매·견적 문의</Button>
            <a className={s.ctaGhost} onClick={() => nav('/support/consult')}>현장실사 상담</a>
          </div>
        </Reveal>
      </section>

      {/* 최종 반복 CTA */}
      <section className={`${s.section} ${s.finalCta}`}>
        <Reveal>
          <h2 className={s.h2c}>구성이 고민되시나요?</h2>
          <p className={s.sub}>시설 종류와 수량을 알려주시면 계측기·게이트웨이 구성을 제안해 드립니다.</p>
          <div className={s.ctaRow}>
            <Button variant="pill" onClick={() => nav('/support/inquiry?product=' + encodeURIComponent('환경 계측기') + '&from=/products/instruments')}>구매·견적 문의</Button>
            <a className={s.ctaGhost} onClick={() => nav('/support/consult')}>현장실사 상담</a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
